import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Badge } from "../components/ui/badge";
import Pagination from "../components/Pagination";
import {
  Wallet, CreditCard, Bank, Check, Spinner, Clock, X, Warning,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { getErrorMessage } from "../lib/utils";
import { useTranslation } from "react-i18next";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TopupPage = () => {
  const { user, refreshUser } = useAuth();
  const { t } = useTranslation();
  const [amount, setAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("bank");
  const [referenceNumber, setReferenceNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [pendingResult, setPendingResult] = useState(null);
  const [bankInfo, setBankInfo] = useState(null);
  const [myRequests, setMyRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(true);
  const [meterStatus, setMeterStatus] = useState(null);
  const [topupLimits, setTopupLimits] = useState({ min: 10, max: 1000 });
  const [customerPaymentMode, setCustomerPaymentMode] = useState("both");
  const [debtInfo, setDebtInfo] = useState(null);
  const [presetAmounts, setPresetAmounts] = useState([50, 100, 200, 500]);
  const [pastPage, setPastPage] = useState(1);
  const [pastPageSize, setPastPageSize] = useState(10);

  const fetchDebtInfo = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/fixed-expenses/tenant-debt-check`);
      setDebtInfo(res.data);
    } catch { /* ignore */ }
  }, []);

  const fetchMeterStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/my-meter-status`);
      setMeterStatus(res.data);
    } catch { /* ignore */ }
  }, []);

  const fetchBankInfo = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/my-customer-bank-info`);
      setBankInfo(res.data);
      if (res.data.topup_min_amount || res.data.topup_max_amount) {
        setTopupLimits({
          min: res.data.topup_min_amount || 10,
          max: res.data.topup_max_amount || 1000,
        });
      }
      if (res.data.topup_preset_amounts && res.data.topup_preset_amounts.length > 0) {
        setPresetAmounts(res.data.topup_preset_amounts);
      }
      if (res.data.payment_mode) {
        setCustomerPaymentMode(res.data.payment_mode);
        // Musteri sadece havale secmisse, varsayilani bank yap
        if (res.data.payment_mode === "manual") {
          setPaymentMethod("bank");
        }
      }
    } catch { /* ignore */ }
  }, []);

  const fetchMyRequests = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/my-topup-requests`);
      setMyRequests(res.data || []);
    } catch { /* ignore */ }
    finally { setLoadingRequests(false); }
  }, []);

  useEffect(() => {
    fetchBankInfo();
    fetchMyRequests();
    fetchMeterStatus();
    fetchDebtInfo();
  }, [fetchBankInfo, fetchMyRequests, fetchMeterStatus, fetchDebtInfo]);

  const handleTopup = async (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (!amount || amt <= 0) {
      toast.error("Gecerli bir tutar girin");
      return;
    }
    if (amt < topupLimits.min) {
      toast.error(`Minimum yukleme tutari: ${topupLimits.min} TL`);
      return;
    }
    if (amt > topupLimits.max) {
      toast.error(`Maksimum yukleme tutari: ${topupLimits.max} TL`);
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/topup`, {
        amount: parseFloat(amount),
        payment_method: paymentMethod,
        reference_number: referenceNumber || null,
      });
      if (response.data.success) {
        if (response.data.status === "pending") {
          setPendingResult(response.data);
          toast.success("Havale talebi olusturuldu!");
          fetchMyRequests();
        } else {
          setSuccess(true);
          toast.success("kWh basariyla yuklendi!");
          refreshUser();
          setTimeout(() => { setSuccess(false); setAmount(""); }, 3000);
        }
      }
    } catch (error) {
      toast.error(getErrorMessage(error, "Odeme islemi basarisiz"));
    } finally { setLoading(false); }
  };

  if (success) {
    return (
      <div className="max-w-lg mx-auto" data-testid="topup-success">
        <Card className="border-emerald-200 bg-emerald-50 shadow-sm">
          <CardContent className="py-12 text-center">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check size={40} weight="bold" className="text-emerald-600" />
            </div>
            <h2 className="text-2xl font-heading font-bold text-emerald-900 mb-2">Odeme Basarili!</h2>
            <p className="text-emerald-700 mb-4">{amount} TL tutarinda kWh sayaciniza yuklendi.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (pendingResult) {
    return (
      <div className="max-w-lg mx-auto" data-testid="topup-pending-result">
        <Card className="border-amber-200 bg-amber-50 shadow-sm">
          <CardContent className="py-12 text-center">
            <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Clock size={40} weight="bold" className="text-amber-600" />
            </div>
            <h2 className="text-2xl font-heading font-bold text-amber-900 mb-2">
              {paymentMethod === "cash" ? "Nakit Odeme Bildirimi Gonderildi" : "Havale Talebi Olusturuldu"}
            </h2>
            <p className="text-amber-700 mb-4">
              {amount} TL {paymentMethod === "cash" ? "nakit odeme" : "havale"} talebiniz yoneticiye iletildi.
            </p>
            <p className="text-sm text-amber-600 mb-6">
              {paymentMethod === "cash"
                ? "Yonetici/personel onayi sonrasi kWh sayaciniza yuklenecektir."
                : "Yonetici havaleyi dogruladiktan sonra kWh sayaciniza yuklenecektir."
              }
            </p>
            {bankInfo?.bank_iban && (
              <div className="bg-white rounded-lg p-4 border border-amber-200 mb-4 text-left">
                <p className="text-xs text-slate-500 mb-1">Havale yapilacak IBAN</p>
                <p className="font-mono text-sm font-bold text-slate-800 select-all">{bankInfo.bank_iban}</p>
                {bankInfo.company_name && <p className="text-xs text-slate-500 mt-1">Alici: {bankInfo.company_name}</p>}
              </div>
            )}
            <Button variant="outline" onClick={() => { setPendingResult(null); setAmount(""); }} data-testid="topup-new-request-btn">
              Yeni Talep Olustur
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const pendingRequests = myRequests.filter(r => r.status === "pending");
  const pastRequests = myRequests.filter(r => r.status !== "pending").slice(0, 10);

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="topup-page">
      {/* Debt warning */}
      {debtInfo?.has_debt && (
        <Card className="border-2 border-red-200 bg-red-50/50 shadow-sm">
          <CardContent className="p-4 flex items-start gap-3">
            <Warning size={24} className="text-red-600 shrink-0 mt-0.5" />
            <div>
              <p className="font-bold text-red-800">
                {debtInfo.invoice_count} odenmemis sabit gider faturaniz var - Toplam: {debtInfo.total_debt.toFixed(2)} TL
              </p>
              <p className="text-sm text-red-600 mt-1">
                Yuklediginiz tutardan once borcunuz kesilecek, kalan miktar kWh olarak sayaciniza yuklenecektir.
              </p>
              <p className="text-xs text-red-500 mt-1">
                Ornegin {topupLimits.min} TL yuklerseniz: {Math.min(debtInfo.total_debt, topupLimits.min).toFixed(2)} TL borc kesintisi + {Math.max(0, topupLimits.min - debtInfo.total_debt).toFixed(2)} TL kWh sayaciniza
              </p>
            </div>
          </CardContent>
        </Card>
      )}
      {/* Meter Balance */}
      <Card className={`shadow-sm ${meterStatus?.success && meterStatus?.balance_energy_kwh > 5 ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'}`}>
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${meterStatus?.success && meterStatus?.balance_energy_kwh > 5 ? 'bg-emerald-100' : 'bg-amber-100'}`}>
              <Wallet size={28} weight="duotone" className={meterStatus?.success && meterStatus?.balance_energy_kwh > 5 ? "text-emerald-600" : "text-amber-600"} />
            </div>
            <div>
              <p className="text-sm font-medium text-slate-600">Sayac Bakiyesi</p>
              <p className="text-3xl font-bold text-slate-900">
                {meterStatus?.success ? (meterStatus.balance_energy_kwh || 0).toFixed(2) : "--"} kWh
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pending Requests Alert */}
      {pendingRequests.length > 0 && (
        <Card className="border-amber-300 bg-amber-50 shadow-sm" data-testid="pending-requests-alert">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Clock size={20} className="text-amber-600" />
              <h3 className="font-semibold text-amber-800">Onay Bekleyen Talepler ({pendingRequests.length})</h3>
            </div>
            <div className="space-y-2">
              {pendingRequests.map(req => (
                <div key={req.id} className="flex items-center justify-between bg-white rounded-lg p-3 border border-amber-200">
                  <div>
                    <span className="font-bold text-slate-800">{req.amount?.toFixed(2)} TL</span>
                    <Badge className={`ml-2 text-xs ${req.payment_method === "cash" ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"}`}>
                      {req.payment_method === "cash" ? "Nakit" : "Havale"}
                    </Badge>
                    {req.reference_number && (
                      <span className="text-xs text-slate-500 ml-2">Ref: {req.reference_number}</span>
                    )}
                    <span className="text-xs text-slate-500 ml-2">{new Date(req.created_at).toLocaleDateString("tr-TR")}</span>
                  </div>
                  <Badge className="bg-amber-100 text-amber-700">{t("dashboard.waiting_approval")}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Topup Form */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-xl font-heading">{t("topup.title")}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleTopup} className="space-y-6">
            {/* Amount Selection */}
            <div className="space-y-3">
              <Label>{t("topup.amount_label")}</Label>
              <div className="grid grid-cols-4 gap-3">
                {presetAmounts.map((preset) => (
                  <Button key={preset} type="button"
                    variant={amount === String(preset) ? "default" : "outline"}
                    className={amount === String(preset) ? "bg-indigo-600 hover:bg-indigo-700" : ""}
                    onClick={() => setAmount(String(preset))}
                    data-testid={`preset-amount-${preset}`}
                  >
                    {preset} TL
                  </Button>
                ))}
              </div>
              <div className="relative mt-3">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 font-medium">TL</span>
                <Input type="number" step="0.01" min={topupLimits.min} max={topupLimits.max} placeholder="Ozel tutar girin"
                  value={amount} onChange={(e) => setAmount(e.target.value)}
                  className="pl-10 h-12 text-lg" data-testid="custom-amount-input" />
              </div>
              <p className="text-xs text-slate-500" data-testid="topup-limits-info">
                Min: {topupLimits.min} TL - Max: {topupLimits.max} TL
              </p>
            </div>

            {/* Payment Method */}
            <div className="space-y-3">
              <Label>{t("topup.payment_method")}</Label>
              <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod} className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <RadioGroupItem value="bank" id="bank" className="peer sr-only" />
                  <Label htmlFor="bank"
                    className="flex items-center gap-3 p-4 border-2 rounded-xl cursor-pointer peer-data-[state=checked]:border-indigo-600 peer-data-[state=checked]:bg-indigo-50 hover:bg-slate-50 transition-colors"
                    data-testid="payment-method-bank">
                    <Bank size={24} className="text-emerald-600" />
                    <div>
                      <p className="font-medium text-slate-900">Havale/EFT</p>
                      <p className="text-xs text-slate-500">Banka transferi ile odeme</p>
                    </div>
                  </Label>
                </div>
                <div>
                  <RadioGroupItem value="cash" id="cash" className="peer sr-only" />
                  <Label htmlFor="cash"
                    className="flex items-center gap-3 p-4 border-2 rounded-xl cursor-pointer peer-data-[state=checked]:border-emerald-600 peer-data-[state=checked]:bg-emerald-50 hover:bg-slate-50 transition-colors"
                    data-testid="payment-method-cash">
                    <Wallet size={24} className="text-green-600" />
                    <div>
                      <p className="font-medium text-slate-900">Nakit</p>
                      <p className="text-xs text-slate-500">Nakit odeme bildirimi</p>
                    </div>
                  </Label>
                </div>
                {customerPaymentMode !== "manual" && (
                <div>
                  <RadioGroupItem value="card" id="card" className="peer sr-only" />
                  <Label htmlFor="card"
                    className="flex items-center gap-3 p-4 border-2 rounded-xl cursor-pointer peer-data-[state=checked]:border-indigo-600 peer-data-[state=checked]:bg-indigo-50 hover:bg-slate-50 transition-colors"
                    data-testid="payment-method-card">
                    <CreditCard size={24} className="text-indigo-600" />
                    <div>
                      <p className="font-medium text-slate-900">Kredi Karti</p>
                      <p className="text-xs text-slate-500">Sanal POS ile odeme</p>
                    </div>
                  </Label>
                </div>
                )}
              </RadioGroup>
            </div>

            {/* Bank IBAN Info */}
            {paymentMethod === "bank" && bankInfo?.bank_iban && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4" data-testid="bank-iban-info">
                <p className="text-xs font-medium text-blue-600 mb-1">Havale Yapilacak Hesap</p>
                <p className="font-mono text-sm font-bold text-slate-800 select-all mb-1">{bankInfo.bank_iban}</p>
                {bankInfo.company_name && <p className="text-xs text-slate-500">Alici: {bankInfo.company_name}</p>}
                <p className="text-xs text-blue-600 mt-2">Havaleyi yaptiktan sonra asagidaki butona basin. Yonetici onayi sonrasi kWh sayaciniza yuklenecektir.</p>
              </div>
            )}

            {/* Reference Number for Bank Transfer */}
            {paymentMethod === "bank" && (
              <div className="space-y-2">
                <Label htmlFor="reference">Dekont / Referans No (opsiyonel)</Label>
                <Input id="reference" placeholder="Havale dekont numarasi veya aciklama"
                  value={referenceNumber} onChange={(e) => setReferenceNumber(e.target.value)}
                  className="h-10" data-testid="reference-number-input" />
                <p className="text-xs text-slate-500">Havale isleminin takibi icin dekont veya referans numarasini girebilirsiniz.</p>
              </div>
            )}

            {/* Cash Info */}
            {paymentMethod === "cash" && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4" data-testid="cash-info">
                <p className="text-xs font-medium text-emerald-700 mb-1">Nakit Odeme Bildirimi</p>
                <p className="text-sm text-slate-700">Nakit odemenizi yonetici veya yetkili personele teslim ettikten sonra asagidaki butona basarak bildirim gonderin.</p>
                <p className="text-xs text-emerald-600 mt-2">Yonetici/personel onayi sonrasi kWh sayaciniza yuklenecektir.</p>
              </div>
            )}

            {paymentMethod === "bank" && !bankInfo?.bank_iban && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <p className="text-sm text-amber-700">Mal sahibi henuz banka bilgilerini tanimlamadi. Lutfen iletisime gecin.</p>
              </div>
            )}

            {/* Submit */}
            <Button type="submit"
              className="w-full h-12 bg-indigo-600 hover:bg-indigo-700 text-lg font-medium"
              disabled={loading || !amount}
              data-testid="topup-submit-btn">
              {loading ? (
                <><Spinner size={20} className="animate-spin mr-2" />Isleniyor...</>
              ) : paymentMethod === "bank" ? (
                <><Bank size={20} className="mr-2" />{amount || "0"} TL Havale Bildirimi Gonder</>
              ) : paymentMethod === "cash" ? (
                <><Wallet size={20} className="mr-2" />{amount || "0"} TL Nakit Odeme Bildirimi Gonder</>
              ) : (
                <><Wallet size={20} className="mr-2" />{amount || "0"} TL Yukle</>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Past Requests */}
      {pastRequests.length > 0 && (
        <Card className="border-slate-200 shadow-sm" data-testid="past-requests-card">
          <CardHeader>
            <CardTitle className="text-base">Gecmis Talepler</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {pastRequests.slice((pastPage - 1) * pastPageSize, pastPage * pastPageSize).map(req => (
                <div key={req.id} className="flex items-center justify-between bg-slate-50 rounded-lg p-3">
                  <div>
                    <span className="font-bold text-slate-800">{req.amount?.toFixed(2)} TL</span>
                    <span className="text-xs text-slate-500 ml-2">{new Date(req.created_at).toLocaleDateString("tr-TR")}</span>
                  </div>
                  {req.status === "approved" && <Badge className="bg-emerald-100 text-emerald-700">Onaylandi</Badge>}
                  {req.status === "rejected" && <Badge className="bg-red-100 text-red-700">{t("subscription.rejected")}</Badge>}
                </div>
              ))}
            </div>
            <Pagination total={pastRequests.length} page={pastPage} pageSize={pastPageSize} onPageChange={setPastPage} onPageSizeChange={setPastPageSize} />
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default TopupPage;
